/**
 * Gives every open / customer-switch / close of a modal its own token, so an async continuation started for a
 * customer who is no longer on screen can tell it is stale and refuse to touch shared state, messages, dialogs
 * or callbacks.
 *
 * Call `begin()` once, synchronously, at the very start of the effect that reacts to the modal opening,
 * switching customers, or closing — before anything else runs. Every async handler then captures
 * `current()` as its own token right when it starts, and after each `await` checks `isCurrent(token)` before
 * writing anything. A token captured before the next `begin()` is never current again; only the token from the
 * newest `begin()` is.
 */
export interface ModalSession {
  begin: () => number;
  current: () => number;
  isCurrent: (token: number) => boolean;
}

export function createModalSession(): ModalSession {
  let token = 0;
  return {
    begin: () => {
      token += 1;
      return token;
    },
    current: () => token,
    isCurrent: candidate => candidate === token,
  };
}
